import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, filter, map, merge, of, takeUntil, timer, Subscription } from 'rxjs';

import { Breadcrumb } from 'src/app/core/models/breadcrumb.model';
import {
  selectProjectEntityLoading,
  selectProjectEntityTree,
  selectLatestCreatedId,
  selectLatestCopiedId,
  selectLatestDeletedIdList,
  selectCanAdd
} from '../../store/project-data/project-data.selectors';
import { ProjectDataActions } from '../../store/project-data/project-data.actions';
import type { TreeEntity } from '../../../core/models/entity.model';
import { ProductContextActions } from '../../store/product-context/product-context.actions';
import { selectCanViewAndEditPrices } from 'src/app/auth/store/session/session.selectors';

@Component({
  selector: 'app-edit-project-view',
  templateUrl: './edit-project-view.component.html',
  styleUrls: ['./edit-project-view.component.scss'],
  standalone: false
})
export class EditProjectViewComponent implements OnInit, OnDestroy {
  projectEntity$: Observable<TreeEntity>;
  loading$: Observable<boolean>;

  latestCreatedId$: Observable<string | null>;
  latestCreatedIdSubscription: Subscription;
  latestCopiedId$: Observable<string | null>;
  latestCopiedIdSubscription: Subscription;
  latestDeletedIdList$: Observable<string[]>;
  latestDeletedIdListSubscription: Subscription;
  canViewAndEditPrices$: Observable<boolean>;
  canAddHood$: Observable<boolean>;

  private routeParamsSubscription: Subscription;

  projectId = '';
  showLoading$ = of(false);

  breadcrumbs: Breadcrumb[] = [];
  currentBreadcrumb: Breadcrumb = { url: '', text: '', translatable: false, isCurrent: true };

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.store.dispatch(ProductContextActions.set_context({ context: 'Project' }));
    this.projectEntity$ = this.store.select(selectProjectEntityTree);
    this.loading$ = this.store.select(selectProjectEntityLoading);
    this.latestCreatedId$ = this.store.select(selectLatestCreatedId);
    this.latestCopiedId$ = this.store.select(selectLatestCopiedId);
    this.latestDeletedIdList$ = this.store.select(selectLatestDeletedIdList);
    this.canViewAndEditPrices$ = this.store.select(selectCanViewAndEditPrices);
    this.canAddHood$ = this.store.select(selectCanAdd(this.projectId, 'GastroFlowHood'));

    this.routeParamsSubscription = this.route.paramMap.subscribe(params => {
      const projectId = params.get('id');
      if (this.projectId !== projectId) {
        this.projectId = projectId;
        this.loadProject();
      }
    });

    const resultReceived = this.loading$.pipe(filter(result => !result));

    this.showLoading$ = merge(
      timer(350).pipe(
        map(() => true),
        takeUntil(resultReceived)
      ),
      resultReceived.pipe(map(() => false))
    );
  }

  ngOnInit() {
    this.watchLatestCreatedProduct();
    this.watchLatestCopiedProduct();
    this.watchLatestDeletedProducts();
  }

  ngOnDestroy(): void {
    if (this.latestCreatedIdSubscription) {
      this.latestCreatedIdSubscription.unsubscribe();
    }
    if (this.latestCopiedIdSubscription) {
      this.latestCopiedIdSubscription.unsubscribe();
    }
    if (this.latestDeletedIdListSubscription) {
      this.latestDeletedIdListSubscription.unsubscribe();
    }
    this.routeParamsSubscription?.unsubscribe();
  }

  watchLatestCreatedProduct() {
    /*
     * When a new product is created, navigate to product view with latest created product selected;
     */
    this.latestCreatedIdSubscription = this.latestCreatedId$
      .pipe(
        map(id => {
          if (id !== null) {
            this.store.dispatch(ProjectDataActions.reset_latest_created_id());
            this.router.navigate(['/projects', this.projectId, 'products'], {
              queryParams: { selectedId: id }
            });
          }
        })
      )
      .subscribe();
  }

  watchLatestCopiedProduct() {
    /*
     * When a product is copied, update project data;
     */
    this.latestCopiedIdSubscription = this.latestCopiedId$
      .pipe(
        map(id => {
          if (id !== null) {
            this.store.dispatch(ProjectDataActions.reset_latest_copied_id());
            this.store.dispatch(ProjectDataActions.get_project_start({ id: this.projectId }));
          }
        })
      )
      .subscribe();
  }

  watchLatestDeletedProducts() {
    /*
     * When a product is deleted, update project data;
     */
    this.latestDeletedIdListSubscription = this.latestDeletedIdList$
      .pipe(
        map(idList => {
          if (idList.length > 0) {
            this.store.dispatch(ProjectDataActions.reset_latest_deleted_id());
            this.store.dispatch(ProjectDataActions.get_project_start({ id: this.projectId }));
          }
        })
      )
      .subscribe();
  }

  getProjectName(projectEntity: TreeEntity) {
    return projectEntity?.Properties['Name']?.Value as string;
  }

  setBreadcrumbs() {
    this.projectEntity$.subscribe((projectEntity: TreeEntity) => {
      const nameProperty = projectEntity?.Properties['Name'];
      const name = (nameProperty?.Value as string) || '';
      if (name) {
        this.currentBreadcrumb.text = name;
        this.currentBreadcrumb.translatable = false;
      } else {
        this.currentBreadcrumb.text = '-';
        this.currentBreadcrumb.translatable = false;
      }
      this.currentBreadcrumb.url = `/projects/${this.projectId}`;
      this.breadcrumbs = [
        { url: '/projects', text: 'UI.Common.Breadcrumb.Projects', translatable: true, isCurrent: false },
        this.currentBreadcrumb
      ];
    });
  }

  addProduct() {
    this.store.dispatch(
      ProjectDataActions.create_entity_start({
        projectId: this.projectId,
        entityId: this.projectId,
        productTypeId: 'GastroFlowHood',
        productName: null,
        defaultValues: []
      })
    );
  }

  private loadProject(): void {
    this.store.dispatch(ProjectDataActions.get_project_start({ id: this.projectId }));
    this.setBreadcrumbs();
  }
}
